import { ChevronDown, ChevronUp, Copy, Download, Terminal } from "lucide-react";
import type React from "react";
import { useEffect, useState } from "react";
import { API_BASE } from "../../api/config";
import "./CliInfo.css";

const CLI_DOWNLOAD_BASE = `${API_BASE}/api/v1/static/cli`;

export const CliInfo: React.FC = () => {
	const currentUrl = window.location.origin;
	const effectiveApiBase = API_BASE || currentUrl;

	const [shContent, setShContent] = useState<string>("Loading...");
	const [psContent, setPsContent] = useState<string>("Loading...");
	const [showSh, setShowSh] = useState(false);
	const [showPs, setShowPs] = useState(false);

	useEffect(() => {
		fetch(`${CLI_DOWNLOAD_BASE}/install.sh`)
			.then((res) => res.text())
			.then(setShContent)
			.catch(() => setShContent("Failed to load install.sh"));

		fetch(`${CLI_DOWNLOAD_BASE}/install.ps1`)
			.then((res) => res.text())
			.then(setPsContent)
			.catch(() => setPsContent("Failed to load install.ps1"));
	}, []);

	const copyToClipboard = (text: string) => {
		navigator.clipboard.writeText(text);
	};

	return (
		<div className="cli-info-container">
			<header className="cli-info-header">
				<Terminal size={24} color="#3b82f6" />
				<h2>af CLI</h2>
			</header>

			<p className="cli-info-description">
				The <code>af</code> utility is a high-velocity terminal tool for
				managing your notes. It features a real-time TUI explorer, fuzzy search,
				and secure keychain integration.
			</p>

			<section className="cli-section">
				<h3>🍎 macOS & 🐧 Linux (Automated)</h3>
				<p>
					Install or update using our automated script. This will place the{" "}
					<code>af</code> binary in <code>/usr/local/bin</code>.
				</p>
				<div className="cli-code-block">
					<code>
						curl -fsSL {effectiveApiBase}/api/v1/static/cli/install.sh | bash
					</code>
					<button
						type="button"
						className="cli-copy-btn"
						onClick={() =>
							copyToClipboard(
								`curl -fsSL ${effectiveApiBase}/api/v1/static/cli/install.sh | bash`,
							)
						}
						title="Copy to clipboard"
					>
						<Copy size={14} />
					</button>
				</div>

				<div className="cli-script-viewer">
					<button
						type="button"
						className="cli-toggle-btn"
						onClick={() => setShowSh(!showSh)}
					>
						{showSh ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
						View install.sh
					</button>
					{showSh && (
						<pre className="cli-script-content">
							<code>{shContent}</code>
						</pre>
					)}
				</div>
			</section>

			<section className="cli-section">
				<h3>🪟 Windows (Automated)</h3>
				<p>
					Run this command in <strong>PowerShell</strong> to install. It will
					add <code>af</code> to your User PATH.
				</p>
				<div className="cli-code-block">
					<code>
						iwr -useb {effectiveApiBase}/api/v1/static/cli/install.ps1 | iex
					</code>
					<button
						type="button"
						className="cli-copy-btn"
						onClick={() =>
							copyToClipboard(
								`iwr -useb ${effectiveApiBase}/api/v1/static/cli/install.ps1 | iex`,
							)
						}
						title="Copy to clipboard"
					>
						<Copy size={14} />
					</button>
				</div>

				<div className="cli-script-viewer">
					<button
						type="button"
						className="cli-toggle-btn"
						onClick={() => setShowPs(!showPs)}
					>
						{showPs ? <ChevronUp size={16} /> : <ChevronDown size={16} />}
						View install.ps1
					</button>
					{showPs && (
						<pre className="cli-script-content">
							<code>{psContent}</code>
						</pre>
					)}
				</div>
			</section>

			<section className="cli-setup-guide">
				<h3>🚀 Velocity Guide</h3>
				<ul className="cli-steps">
					<li>
						<div className="cli-step-number">1</div>
						<div className="cli-step-content">
							<p>
								<strong>Login:</strong> <code>af login</code>
							</p>
						</div>
					</li>
					<li>
						<div className="cli-step-number">2</div>
						<div className="cli-step-content">
							<p>
								<strong>Browse & Act:</strong> <code>af ls</code>
							</p>
							<p className="cli-note">
								Use arrows to navigate, 'r' to read, 'e' to edit.
							</p>
						</div>
					</li>
					<li>
						<div className="cli-step-number">3</div>
						<div className="cli-step-content">
							<p>
								<strong>Rapid Add:</strong> <code>af a -t "Idea"</code>
							</p>
						</div>
					</li>
					<li>
						<div className="cli-step-number">4</div>
						<div className="cli-step-content">
							<p>
								<strong>Search:</strong> <code>af s "project"</code>
							</p>
						</div>
					</li>
				</ul>
			</section>

			<section className="cli-section">
				<h3>📦 Manual Downloads</h3>
				<div className="cli-platforms">
					<div className="cli-platform-card">
						<span className="cli-platform-name">🍎 macOS</span>
						<a
							href={`${CLI_DOWNLOAD_BASE}/af-macos-arm64`}
							className="cli-download-link"
							download
						>
							<Download size={14} /> Download
						</a>
					</div>
					<div className="cli-platform-card">
						<span className="cli-platform-name">🐧 Linux</span>
						<a
							href={`${CLI_DOWNLOAD_BASE}/af-linux`}
							className="cli-download-link"
							download
						>
							<Download size={14} /> Download
						</a>
					</div>
					<div className="cli-platform-card">
						<span className="cli-platform-name">🪟 Windows</span>
						<a
							href={`${CLI_DOWNLOAD_BASE}/af-windows.exe`}
							className="cli-download-link"
							download
						>
							<Download size={14} /> Download
						</a>
					</div>
				</div>
			</section>
		</div>
	);
};
